import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Globe, Loader2, Mail, MapPin, Phone } from 'lucide-react';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  ['trading_name', 'company.f.trading', Building2],
  ['legal_entity', 'company.f.legal', Building2],
  ['registered_address', 'company.f.address', MapPin],
  ['country_of_registration', 'company.f.country', Globe],
  ['registration_number', 'company.f.reg', Building2],
  ['tax_number', 'company.f.tax', Building2],
  ['website', 'company.f.website', Globe],
  ['general_enquiries', 'company.f.general', Mail],
  ['customer_support', 'company.f.support', Mail],
  ['phone_whatsapp', 'company.f.phone', Phone],
  ['partner_enquiries', 'company.f.partners', Mail],
];

export default function CompanyInfoPage() {
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  useSEO(`${t('company.title')} | Eco Collection`, t('company.sub'));

  useEffect(() => {
    fetch(`${API}/legal/company`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setInfo(d))
      .catch(() => setInfo({}));
  }, []);

  return (
    <div data-testid="company-info-page">
      <PageHeader kicker="Eco Collection" title={t('company.title')} sub={t('company.sub')} />
      <section className="bg-eco-cream py-12">
        <div className="eco-container max-w-3xl">
          {!info ? (
            <div className="grid place-items-center py-10"><Loader2 className="animate-spin text-eco-green" size={26} /></div>
          ) : (
            <>
              <Reveal>
                <div className="rounded-2xl border border-eco-ink/10 bg-white p-6 md:p-8">
                  <dl className="divide-y divide-eco-ink/8">
                    {FIELDS.map(([key, labelKey, Icon]) => {
                      const value = info[key];
                      const pending = !value || value === 'TO_BE_ADDED';
                      return (
                        <div key={key} data-testid={`company-field-${key}`} className="flex items-start gap-4 py-3.5 first:pt-0 last:pb-0">
                          <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-eco-cream text-eco-green"><Icon size={14} /></span>
                          <div className="min-w-0">
                            <dt className="eco-field-label">{t(labelKey)}</dt>
                            {pending ? (
                              <dd data-testid={`company-value-${key}`} className="mt-1 inline-flex rounded-full bg-eco-gold/10 px-2.5 py-0.5 text-xs font-semibold text-eco-gold">{t('company.tba')}</dd>
                            ) : key === 'website' ? (
                              <dd data-testid={`company-value-${key}`} className="mt-1 text-sm font-medium text-eco-ink"><a href={`https://${value}`} target="_blank" rel="noopener noreferrer" className="hover:text-eco-green">{value}</a></dd>
                            ) : key === 'phone_whatsapp' ? (
                              <dd data-testid={`company-value-${key}`} className="mt-1 text-sm font-medium text-eco-ink"><a href={`tel:${String(value).replace(/\s/g, '')}`} className="hover:text-eco-green">{value}</a></dd>
                            ) : key.includes('enquiries') || key === 'customer_support' ? (
                              <dd data-testid={`company-value-${key}`} className="mt-1 text-sm font-medium text-eco-ink"><a href={`mailto:${value}`} className="hover:text-eco-green">{value}</a></dd>
                            ) : (
                              <dd data-testid={`company-value-${key}`} className="mt-1 text-sm font-medium text-eco-ink">{value}</dd>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </dl>
                </div>
              </Reveal>
              <Reveal>
                <p className="mt-5 text-xs text-eco-ink/50" data-testid="company-note">{t('company.note')}</p>
              </Reveal>
              <Reveal>
                <Link to="/contact" data-testid="company-contact-cta" className="eco-btn-secondary mt-6 inline-flex"><ArrowLeft size={14} /> {t('foot.sup.contact')}</Link>
              </Reveal>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
